import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

const serviceTypeOptions = [
  { value: 'ACCOMMODATION', label: 'Accommodation' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'MEAL', label: 'Meal' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'OTHER', label: 'Other' },
] as const;

type ServiceType = typeof serviceTypeOptions[number]['value'];

interface QuoteItemFormData {
  packageId?: string;
  itemType: ServiceType;
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
}

interface QuoteItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId: string;
  onSuccess?: () => void;
}

export function QuoteItemModal({
  open,
  onOpenChange,
  quoteId,
  onSuccess,
}: QuoteItemModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<QuoteItemFormData>({
    defaultValues: {
      itemType: 'OTHER',
      quantity: 1,
      unitPrice: 0,
    },
  });

  const { data: packages } = trpc.package.list.useQuery({
    page: 1,
    status: 'PUBLISHED',
  });

  const utils = trpc.useUtils();

  const addItemMutation = trpc.quote.addItem.useMutation({
    onSuccess: () => {
      utils.quote.byId.invalidate(quoteId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const selectedPackageId = watch('packageId');
  const quantity = watch('quantity') || 1;
  const unitPrice = watch('unitPrice') || 0;
  const totalPrice = quantity * unitPrice;

  const handlePackageSelect = (packageId: string) => {
    setValue('packageId', packageId === 'none' ? undefined : packageId);

    if (packageId !== 'none' && packages?.packages) {
      const pkg = packages.packages.find(p => p.id === packageId);
      if (pkg) {
        setValue('name', pkg.name);
        setValue('itemType', 'OTHER');
        // Set price from the first pricing if available
        const firstPricing = pkg.pricing?.[0];
        if (firstPricing) {
          setValue('unitPrice', Number(firstPricing.price2Pax));
        }
      }
    }
  };

  const onSubmit = (data: QuoteItemFormData) => {
    setIsSubmitting(true);
    addItemMutation.mutate({
      quoteId,
      packageId: data.packageId,
      itemType: data.itemType,
      name: data.name,
      description: data.description,
      quantity: data.quantity,
      unitPrice: data.unitPrice,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Quote Item</DialogTitle>
          <DialogDescription>
            Add a service or package to this quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Package Selection (Optional) */}
          <div className="space-y-2">
            <Label>Link to Package (Optional)</Label>
            <Combobox
              options={[
                { value: 'none', label: 'No package' },
                ...(packages?.packages.map((pkg) => ({
                  value: pkg.id,
                  label: `${pkg.code} - ${pkg.name}`,
                })) ?? []),
              ]}
              value={selectedPackageId || 'none'}
              onValueChange={handlePackageSelect}
              placeholder="Select a package"
              searchPlaceholder="Search packages..."
            />
          </div>

          {/* Service Type */}
          <div className="space-y-2">
            <Label>Service Type *</Label>
            <Combobox
              options={serviceTypeOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={watch('itemType') || 'OTHER'}
              onValueChange={(value) => setValue('itemType', value as ServiceType)}
              placeholder="Select type"
              searchPlaceholder="Search..."
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Item Name *</Label>
            <Input
              id="name"
              {...register('name', { required: 'Name is required' })}
              placeholder="e.g., Hotel Accommodation - 3 nights"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Optional description..."
              rows={2}
            />
          </div>

          {/* Quantity and Price */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                {...register('quantity', {
                  required: 'Quantity is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Minimum quantity is 1' },
                })}
              />
              {errors.quantity && (
                <p className="text-sm text-red-500">{errors.quantity.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unitPrice">Unit Price *</Label>
              <Input
                id="unitPrice"
                type="number"
                step="0.01"
                min="0"
                {...register('unitPrice', {
                  required: 'Price is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Price cannot be negative' },
                })}
              />
              {errors.unitPrice && (
                <p className="text-sm text-red-500">{errors.unitPrice.message}</p>
              )}
            </div>
          </div>

          {/* Total Preview */}
          <div className="rounded-lg bg-muted p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Price</span>
              <span className="font-medium text-lg">
                ${totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* Error Messages */}
          {addItemMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {addItemMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
